import React, { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useLocalStorage from 'react-localstorage-hook';

import Typography from '@mui/material/Typography';
import { Address } from 'viem';

import Button from '@components/common/Button';
import Input from '@components/common/Input';

import { ZERO_ADDRESS } from '@constants/common/chain.constants';
import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { VIEW_ADDRESS } from '@constants/common/localStorage.constants';
import { Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Address]: string;
};

interface Props {
  address: Address | undefined;
}

const ViewAs: FC<Props> = ({ address }) => {
  const { classes } = useStyles();
  const [viewAddress, setViewAddress] = useLocalStorage(VIEW_ADDRESS);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    defaultValues: { [FieldNames.Address]: viewAddress || '' },
  });

  const onSubmit = useCallback(
    (formData: FormValues) => {
      setViewAddress(formData[FieldNames.Address]);
    },
    [viewAddress],
  );

  const handleRemove = useCallback(() => {
    setViewAddress(null);
    reset();
  }, [viewAddress]);

  return (
    <div>
      <Typography variant="body1" className={classes.text}>
        View as: {viewAddress || address}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Controller
          control={control}
          name={FieldNames.Address}
          render={({ field: { value, onBlur, onChange } }) => (
            <Input
              value={value}
              isDark
              onBlur={onBlur}
              onChange={(e) => onChange(e)}
              placeholder={ZERO_ADDRESS}
            />
          )}
        />

        <Button buttonType="submit">View</Button>
        <Button
          buttonType="button"
          variant={Variant.Secondary}
          disabled={!viewAddress}
          onClick={handleRemove}
        >
          Remove
        </Button>
      </form>
    </div>
  );
};

export default ViewAs;
